// PersonInfo.tsx
import React, { useState } from 'react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { FormErrors } from "./[type]/FormErrors";
import { FormData } from "./[type]/FormData";
import { PersonInfoProps } from "./[type]/PersonInfoProps";

const PersonInfo: React.FC<PersonInfoProps> = ({ onComplete, onGoBack, preFilledEmail }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    nickname: '',
    birthDate: '',
    phone: '',
    username: '',
    password: '',
    confirmPassword: '',
    email: preFilledEmail, // Pre-filled email
    emailDomain: '직접입력',
    referralCode: '',
    marketingSMS: false,
    marketingEmail: false,
    marketingDM: false,
    marketingTM: false,
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setFormData((prev) => ({
        ...prev,
        [name]: target.checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e);
    if (!passwordTouched) {
      setPasswordTouched(true);
    }
  };

  const handlePhoneChange = (value: string | undefined) => {
    setFormData((prev) => ({
      ...prev,
      phone: value,
    }));
    if (!phoneTouched) {
      setPhoneTouched(true);
    }
  };

  const validate = (): FormErrors => {
    const errors: FormErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      errors.name = '이름을 입력하세요.';
    }

    // Nickname validation
    if (!formData.nickname.trim()) {
      errors.nickname = '닉네임을 입력하세요.';
    }

    // Birthdate validation
    if (!formData.birthDate) {
      errors.birthDate = '생년월일을 입력하세요.';
    }

    // Phone validation
    if (!formData.phone) {
      errors.phone = '휴대폰 번호를 입력하세요.';
    } else if (!isValidPhoneNumber(formData.phone)) {
      errors.phone = '유효한 휴대폰 번호를 입력하세요.';
    }

    // Username validation
    if (!formData.username.trim()) {
      errors.username = '아이디를 입력하세요.';
    }

    // Password validation
    if (!formData.password) {
      errors.password = '비밀번호를 입력하세요.';
    } else {
      const passwordErrors = [];

      if (formData.password.length < 10) {
        passwordErrors.push('비밀번호는 최소 10자 이상이어야 합니다.');
      }

      if (!/[A-Z]/.test(formData.password)) {
        passwordErrors.push('비밀번호에 대문자가 포함되어야 합니다.');
      }

      if (!/[a-z]/.test(formData.password)) {
        passwordErrors.push('비밀번호에 소문자가 포함되어야 합니다.');
      }

      if (!/\d/.test(formData.password)) {
        passwordErrors.push('비밀번호에 숫자가 포함되어야 합니다.');
      }

      if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
        passwordErrors.push('비밀번호에 특수문자가 포함되어야 합니다.');
      }

      if (/\s/.test(formData.password)) {
        passwordErrors.push('비밀번호에 공백이 포함될 수 없습니다.');
      }

      if (passwordErrors.length > 0) {
        errors.password = passwordErrors.join(' ');
      }
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      errors.confirmPassword = '비밀번호 확인을 입력하세요.';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = '비밀번호가 일치하지 않습니다.';
    }

    // Email validation
    if (!formData.email.trim()) {
      errors.email = '이메일을 입력하세요.';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const fullEmail =
          formData.emailDomain === '직접입력'
              ? formData.email
              : `${formData.email}@${formData.emailDomain}`;
      if (!emailRegex.test(fullEmail)) {
        errors.email = '유효한 이메일 주소를 입력하세요.';
      }
    }

    return errors;
  };

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const errors = validate();
    setFormErrors(errors);
    if (Object.keys(errors).length === 0) {
      // No errors, proceed to send data to parent
      setIsLoading(true);
      try {
        await onComplete(formData); // Pass formData to parent
        setSuccess(true);
      } catch (err: any) {
        setError(err.message || '회원가입 과정에서 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
      console.log('최종 회원가입 데이터:', {
        ...formData,
      });
    }
  };

  const getPasswordStrength = () => {
    let strength = 0;
    if (formData.password.length >= 10) strength += 1;
    if (/[A-Z]/.test(formData.password)) strength += 1;
    if (/[a-z]/.test(formData.password)) strength += 1;
    if (/\d/.test(formData.password)) strength += 1;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) strength += 1;
    if (!/\s/.test(formData.password)) strength += 1;

    return strength;
  };

  const passwordStrength = getPasswordStrength();
  const strengthLabels = ['취약', '보통', '강함', '매우 강함', '최고'];

  const handlePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
      <div className="p-6 bg-white mobile:px-4 shadow-md rounded-md max-w-[600px] w-full mt-[300px] mobile:mt-[260px] mb-24 mx-auto">
        {/* 회원 정보 */}
        <div className="mb-6">
          <h2 className="text-2xl mobile:text-base font-bold mb-4">회원정보</h2>
          <div className="grid grid-cols-1 gap-4">
            {/* 이름 입력 필드 */}
            <div className="flex flex-col">
              <label className="w-32 text-sm mobile:text-xs font-medium text-gray-700">
                이름<span className="text-red-500">*</span>
              </label>
              <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`mt-1 block w-full border ${formErrors.name ? 'border-red-500' : 'border-gray-300'
                  } rounded-md shadow-sm p-3 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="이름을 입력하세요"
                  required
              />
              {formErrors.name && (
                  <span className="text-red-500 text-sm mt-1">
                {formErrors.name}
              </span>
              )}
            </div>
            {/* 닉네임 입력 필드 */}
            <div className="flex flex-col">
              <label className="w-32 text-sm mobile:text-xs font-medium text-gray-700">
                닉네임<span className="text-red-500">*</span>
              </label>
              <input
                  type="text"
                  name="nickname"
                  value={formData.nickname}
                  onChange={handleChange}
                  className={`mt-1 block w-full border ${formErrors.nickname ? 'border-red-500' : 'border-gray-300'
                  } rounded-md shadow-sm p-3 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="닉네임을 입력하세요"
                  required
              />
              {formErrors.nickname && (
                  <span className="text-red-500 text-sm mt-1">
                {formErrors.nickname}
              </span>
              )}
            </div>
            {/* 생년월일 입력 필드 */}
            <div className="flex flex-col">
              <label className="w-32 text-sm font-medium mobile:text-xs text-gray-700">
                생년월일<span className="text-red-500">*</span>
              </label>
              <input
                  type="date"
                  name="birthDate"
                  value={formData.birthDate}
                  onChange={handleChange}
                  min="1900-01-01"
                  max={new Date().toISOString().split("T")[0]} // 오늘 날짜
                  className={`mt-1 block w-full border ${formErrors.birthDate ? 'border-red-500' : 'border-gray-300'
                  } rounded-md shadow-sm p-3 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  required
              />
              {formErrors.birthDate && (
                  <span className="text-red-500 text-sm mt-1">
                {formErrors.birthDate}
              </span>
              )}
            </div>

            {/* 휴대폰 입력 필드 */}
            <div className="flex flex-col">
              <label className="w-32 text-sm font-medium mobile:text-xs text-gray-700">
                휴대폰<span className="text-red-500">*</span>
              </label>
              <PhoneInput
                  defaultCountry="KR"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  className={`mt-1 block w-full border ${formErrors.phone ? 'border-red-500' : 'border-gray-300'
                  } rounded-md shadow-sm p-3 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="전화번호를 입력하세요"
                  required
                  international
                  countryCallingCodeEditable={false}
              />
              {formErrors.phone && (
                  <span className="text-red-500 text-sm mt-1">
                {formErrors.phone}
              </span>
              )}
            </div>
          </div>
        </div>

        {/* 개인 정보 입력 */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold mobile:text-base mb-4">개인정보입력</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 아이디 입력 필드 */}
            <div className="flex flex-col">
              <label className="block text-gray-700 mobile:text-xs font-medium">
                아이디<span className="text-red-500">*</span>
              </label>
              <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  className={`mt-1 block w-full border ${formErrors.username ? 'border-red-500' : 'border-gray-300'
                  } rounded-md shadow-sm p-3 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="아이디를 입력하세요"
                  required
              />
              {formErrors.username && (
                  <span className="text-red-500 text-sm mt-1">
                {formErrors.username}
              </span>
              )}
            </div>

            {/* 비밀번호 입력 필드 */}
            <div className="flex flex-col">
              <label className="block text-gray-700 mobile:text-xs font-medium">
                비밀번호<span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handlePasswordChange}
                    onBlur={() => setPasswordTouched(true)}
                    className={`mt-1 block w-full border ${formErrors.password ? 'border-red-500' : 'border-gray-300'
                    } rounded-md shadow-sm p-3 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="비밀번호를 입력하세요"
                    required
                />
                <div
                    onClick={handlePasswordVisibility}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                >
                  {showPassword ? (
                      <AiOutlineEye size={24} className='text-[#8a8a8a]' />
                  ) : (
                      <AiOutlineEyeInvisible size={24} className='text-[#8a8a8a]' />
                  )}
                </div>
              </div>
              {formErrors.password && (
                  <span className="text-red-500 text-sm mt-1">
                {formErrors.password}
              </span>
              )}
              {/* 비밀번호 요구사항 */}
              <div className="mt-2 text-sm">
                <p className='mobile:text-xs'>비밀번호는 다음을 포함해야 합니다:</p>
                <ul className="list-disc list-inside mobile:text-xs mobile:mt-2 mobile:leading-5">
                  <li
                      className={
                        formData.password.length >= 10
                            ? 'text-green-500'
                            : 'text-red-500'
                      }
                  >
                    최소 10자
                  </li>
                  <li
                      className={
                        /[A-Z]/.test(formData.password)
                            ? 'text-green-500'
                            : 'text-red-500'
                      }
                  >
                    대문자
                  </li>
                  <li
                      className={
                        /[a-z]/.test(formData.password)
                            ? 'text-green-500'
                            : 'text-red-500'
                      }
                  >
                    소문자
                  </li>
                  <li
                      className={
                        /\d/.test(formData.password)
                            ? 'text-green-500'
                            : 'text-red-500'
                      }
                  >
                    숫자
                  </li>
                  <li
                      className={
                        /[!@#$%^&*(),.?":{}|<>]/.test(formData.password)
                            ? 'text-green-500'
                            : 'text-red-500'
                      }
                  >
                    특수문자
                  </li>
                  <li
                      className={
                        !/\s/.test(formData.password)
                            ? 'text-green-500'
                            : 'text-red-500'
                      }
                  >
                    공백 없음
                  </li>
                </ul>
              </div>
              {/* 비밀번호 강도 표시 */}
              {passwordTouched && (
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                          className={`h-2.5 rounded-full ${passwordStrength <= 2
                              ? 'bg-red-500'
                              : passwordStrength <= 4
                                  ? 'bg-yellow-500'
                                  : 'bg-green-500'
                          }`}
                          style={{ width: `${(passwordStrength / 6) * 100}%` }}
                      ></div>
                    </div>
                    <p className="text-sm mt-1">
                      비밀번호 강도:{' '}
                      {strengthLabels[
                          Math.min(passwordStrength - 1, strengthLabels.length - 1)
                          ]}
                    </p>
                  </div>
              )}
            </div>

            {/* 비밀번호 확인 입력 필드 */}
            <div className="flex flex-col">
              <label className="block text-gray-700 mobile:text-xs  font-medium">
                비밀번호 확인<span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`mt-1 block w-full border ${formErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                    } rounded-md shadow-sm p-3 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="비밀번호를 다시 입력하세요"
                    required
                />
                <div
                    onClick={handleConfirmPasswordVisibility}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                >
                  {showConfirmPassword ? (
                      <AiOutlineEye size={24} className='text-[#8a8a8a]' />
                  ) : (
                      <AiOutlineEyeInvisible size={24} className='text-[#8a8a8a]' />
                  )}
                </div>
              </div>
              {formErrors.confirmPassword && (
                  <span className="text-red-500 text-sm mt-1">
                {formErrors.confirmPassword}
              </span>
              )}
            </div>

            {/* 이메일 입력 필드 */}
            <div className="flex flex-col">
              <label className="block text-gray-700 mobile:text-xs font-medium">
                이메일<span className="text-red-500">*</span>
              </label>
              <div className="relative flex">
                <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`mt-1 block w-full border ${formErrors.email ? 'border-red-500' : 'border-gray-300'
                    } rounded-md shadow-sm p-3 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="이메일을 입력하세요"
                    required
                    disabled // Email is pre-filled and disabled
                />
              </div>
              {formErrors.email && (
                  <span className="text-red-500 text-sm mt-1">
                {formErrors.email}
              </span>
              )}
            </div>

            {/* 추천인 코드 입력 필드 */}
            <div className="flex flex-col">
              <label className="block text-gray-700 mobile:text-xs font-medium">
                추천인 코드 입력 (선택)
              </label>
              <input
                  type="text"
                  name="referralCode"
                  value={formData.referralCode}
                  onChange={handleChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="추천인이 있는 경우 입력하세요"
              />
            </div>

            {/* 마케팅 정보 수신 */}
            <div className="mt-4 mb-10">
              <h3 className="font-semibold mobile:text-xs mb-2">
                마케팅 정보 수신동의 (선택)
              </h3>
              <div className="flex items-center space-x-4">
                <label className="inline-flex items-center">
                  <input
                      type="checkbox"
                      name="marketingEmail"
                      checked={formData.marketingEmail}
                      onChange={handleChange}
                      className="custom-checkbox form-checkbox h-5 w-5 text-blue-600"
                  />
                  <span className="ml-2 mobile:text-xs">이메일</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                      type="checkbox"
                      name="marketingDM"
                      checked={formData.marketingDM}
                      onChange={handleChange}
                      className="custom-checkbox form-checkbox h-5 w-5 text-blue-600"
                  />
                  <span className="ml-2 mobile:text-xs">디스코드</span>
                </label>
              </div>
            </div>

            {/* 안내 문구 */}
            <div className="font-thin text-sm mobile:text-xs mobile:leading-5 mt-10 leading-6 text-gray-600">
              <p>
                ※ 마케팅 정보 수신에 동의하시면 다양한 혜택을 받으실 수 있습니다.
              </p>
              <p>
                ※ 필수항목에 동의하지 않으실 경우 서비스 가입이 불가합니다.
              </p>
              <p>
                ※ 선택항목에 동의하지 않으셔도 서비스 가입이 가능하나, 관련 서비스는
                제공 받으실 수 없습니다.
              </p>
            </div>

            {/* Buttons */}
            <div className="flex justify-between pt-11">
              <button
                  type="button"
                  onClick={onGoBack}
                  className="bg-gray-300 hover:bg-gray-400 text-white mobile:text-sm font-bold py-2 px-8 mobile:px-4 rounded"
              >
                취소
              </button>
              <button
                  type="submit"
                  disabled={isLoading}
                  className={`${
                      isLoading
                          ? 'bg-blue-300 cursor-not-allowed'
                          : 'bg-MainColor hover:bg-MainColor/80'
                  } text-white font-bold py-2 px-8 mobile:text-sm mobile:px-4 rounded`}
              >
                {isLoading ? '확인 중...' : '확인'}
              </button>
            </div>
          </form>
        </div>

        {/* Success and Error Messages */}
        {success && (
            <div className="text-green-500 text-center mt-4">
              회원가입이 성공적으로 완료되었습니다!
            </div>
        )}
        {error && (
            <div className="text-red-500 text-center mt-4">
              {error}
            </div>
        )}
      </div>
  );
};

export default PersonInfo;
